import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getDirectoryHandle as getProjectDirHandle, saveDirectoryHandle as saveProjectDirHandle, removeDirectoryHandle as removeProjectDirHandle, walkDirectory } from '../utils/workingDirectory';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, BookCheck, FolderOpen, FolderSync,
  RefreshCw, X as XIcon, AlertTriangle, CheckCircle, Info,
  FileText, ChevronDown, ChevronRight,
  ShieldCheck, XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══ Severity icon ═══ */
const SeverityIcon = ({ severity, className = 'w-3.5 h-3.5' }) => {
  if (severity === 'error') return <XCircle className={`${className} text-red-500`} />;
  if (severity === 'warning') return <AlertTriangle className={`${className} text-amber-500`} />;
  return <Info className={`${className} text-blue-500`} />;
};

/* ═══ Rule status badge ═══ */
const RuleBadge = ({ passed, count }) => (
  passed
    ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px] gap-1"><CheckCircle className="w-2.5 h-2.5" /> Pass</Badge>
    : <Badge className="bg-red-100 text-red-700 text-[10px] gap-1"><XCircle className="w-2.5 h-2.5" /> {count} issue{count !== 1 ? 's' : ''}</Badge>
);

/* ═══ Expandable DM result row ═══ */
const DMResultRow = ({ dm, index }) => {
  const [expanded, setExpanded] = useState(false);
  const ruleEntries = Object.entries(dm.rule_results || {});

  return (
    <div className={`border-b border-slate-100 last:border-0 ${dm.compliant ? 'bg-white' : 'bg-red-50/30'}`}
      data-testid={`dm-result-${index}`}>
      {/* Summary row */}
      <div className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50/80 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        {dm.compliant
          ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
          : <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800 truncate">{dm.title || dm.dmc}</p>
          <p className="text-[11px] font-mono text-slate-400 truncate">{dm.dmc}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className="text-[10px]">{dm.schema_type}</Badge>
          <span className="text-[11px] text-slate-400">{dm.word_count}w / {dm.sentence_count}s</span>
          {dm.compliant
            ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Compliant</Badge>
            : <Badge className="bg-red-100 text-red-700 text-[10px]">{dm.violations?.length || 0} violations</Badge>}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-3 pl-11 space-y-2">
          {/* Rules summary */}
          <div className="flex flex-wrap gap-2">
            {ruleEntries.map(([key, r]) => (
              <div key={key} className="flex items-center gap-1.5 text-xs">
                <span className="font-mono text-slate-500">{r.rule || key}</span>
                <span className="text-slate-600">{r.name}</span>
                <RuleBadge passed={r.passed} count={r.count} />
              </div>
            ))}
          </div>

          {/* Individual violations */}
          {dm.violations?.length > 0 && (
            <div className="mt-2 space-y-1 max-h-[200px] overflow-y-auto">
              {dm.violations.map((v, i) => (
                <div key={i} className="flex items-start gap-2 text-xs p-1.5 rounded bg-white border border-slate-100">
                  <SeverityIcon severity={v.severity} className="w-3 h-3 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-slate-700">{v.word}</span>
                    <span className="text-slate-400 ml-2">{v.suggestion}</span>
                  </div>
                  <Badge variant="outline" className="text-[9px] shrink-0">{v.rule}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════
   MAIN STE CHECK PAGE
   ═══════════════════════════════════════════════════════════════ */
const STECheckPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const projects = useProjects();
  const projectState = location.state?.project;

  const [selectedProjectId, setSelectedProjectId] = useState(projectState?.id || 'none');
  const currentProject = projects.find(p => p.id === selectedProjectId);
  const projectId = selectedProjectId !== 'none' ? selectedProjectId : null;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Working directory
  const [dirHandle, setDirHandle] = useState(null);
  const [dirName, setDirName] = useState('');
  const [scanningDir, setScanningDir] = useState(false);
  const [dmFiles, setDmFiles] = useState([]); // [{name, handle}]

  // Check results
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);

  // Scan directory for DM XML files
  const scanDirectory = useCallback(async (handle) => {
    if (!handle) return;
    setScanningDir(true);
    try {
      const dms = [];
      for await (const { entry, path } of walkDirectory(handle)) {
        if (entry.name.toUpperCase().startsWith('DMC-') && entry.name.toLowerCase().endsWith('.xml')) {
          dms.push({ name: entry.name, handle: entry, path });
        }
      }
      dms.sort((a, b) => a.name.localeCompare(b.name));
      setDmFiles(dms);
    } catch {
      setDmFiles([]);
      toast.error('Failed to scan directory');
    }
    setScanningDir(false);
  }, []);

  // Load the working directory linked to the SELECTED PROJECT (Project Settings store)
  useEffect(() => {
    (async () => {
      setDirHandle(null); setDmFiles([]); setDirName(''); setResults(null);
      if (!projectId) return;
      try {
        const record = await getProjectDirHandle(projectId);
        const saved = record?.handle;
        if (saved) {
          setDirHandle(saved);
          setDirName(record.displayName || saved.name);
          const perm = await saved.queryPermission({ mode: 'read' });
          if (perm === 'granted') await scanDirectory(saved);
        }
      } catch { /* no saved handle for this project */ }
    })();
  }, [projectId, scanDirectory]);

  const linkDirectory = async () => {
    if (!window.showDirectoryPicker) {
      toast.error('Use Chrome or Edge for File System Access API.');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ mode: 'read' });
      setDirHandle(handle);
      setDirName(handle.name);
      await scanDirectory(handle);
      if (projectId) {
        try {
          await saveProjectDirHandle(projectId, handle, handle.name);
          await axios.put(`${API}/projects/${projectId}`, { working_directory: handle.name }, { headers: { Authorization: `Bearer ${token}` } });
        } catch { /* non-critical */ }
      }
      toast.success(`Linked to "${handle.name}"`);
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('Failed to access directory');
    }
  };

  const refreshDir = async () => {
    if (!dirHandle) return;
    try {
      let perm = await dirHandle.queryPermission({ mode: 'read' });
      if (perm !== 'granted') {
        perm = await dirHandle.requestPermission({ mode: 'read' });
        if (perm !== 'granted') { toast.error('Permission denied'); return; }
      }
      await scanDirectory(dirHandle);
      toast.success('Directory refreshed');
    } catch {
      toast.error('Directory access lost');
      setDirHandle(null); setDmFiles([]); setDirName('');
    }
  };

  const unlinkDir = async () => {
    setDirHandle(null); setDmFiles([]); setDirName(''); setResults(null);
    if (projectId) {
      try {
        await removeProjectDirHandle(projectId);
        await axios.put(`${API}/projects/${projectId}`, { working_directory: '' }, { headers: { Authorization: `Bearer ${token}` } });
      } catch { /* non-critical */ }
    }
    toast.info('Working directory unlinked');
  };

  // Run STE check on all DM files
  const runCheck = async () => {
    if (!dirHandle) { toast.error('Link a working directory first'); return; }
    if (!projectId) { toast.error('Select a project first'); return; }
    if (dmFiles.length === 0) { toast.error('No DM files found in directory'); return; }

    setChecking(true);
    setResults(null);
    toast.info(`Reading ${dmFiles.length} DM files...`);

    try {
      const dms = [];
      for (const dm of dmFiles) {
        try {
          const file = await dm.handle.getFile();
          const text = await file.text();
          // Extract DMC code from filename
          const dmc = dm.name.replace(/\.xml$/i, '');
          dms.push({ dmc, xml_content: text });
        } catch {
          // Skip unreadable files
        }
      }

      if (dms.length === 0) {
        toast.error('Could not read any DM files');
        setChecking(false);
        return;
      }

      toast.info(`Checking ${dms.length} DMs against STE Issue 9 rules...`);

      const res = await axios.post(`${API}/ste-check/check-dms`, {
        project_id: projectId,
        dms,
      }, { headers, timeout: 60000 });

      setResults(res.data);

      const ov = res.data.overall;
      if (ov.compliance_pct === 100) {
        toast.success(`All ${ov.total_dms} DMs are STE compliant!`);
      } else {
        toast.warning(`${ov.compliant_dms}/${ov.total_dms} DMs compliant (${ov.compliance_pct}%)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'STE check failed');
    }
    setChecking(false);
  };

  // ═══ NO PROJECT ═══
  if (!projectId) {
    return (
      <div className="space-y-6" data-testid="ste-check-page">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard/projects')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </button>
        </div>
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>STE Check</h1>
          <Select value="none" onValueChange={(v) => setSelectedProjectId(v)}>
            <SelectTrigger className="w-[280px]" data-testid="ste-check-project-select">
              <SelectValue placeholder="Select a project..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select a project...</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6 text-center">
            <BookCheck className="w-12 h-12 text-amber-600 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>No Project Selected</h2>
            <p className="text-amber-600 mt-1">Select a project to check DMs against ASD-STE100 Issue 9.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overall = results?.overall;

  // ═══ MAIN LAYOUT ═══
  return (
    <div className="space-y-4" data-testid="ste-check-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <button onClick={() => navigate(projectId ? `/dashboard/projects/${projectId}` : '/dashboard/projects')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Project
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>STE Check</h1>
          <p className="text-sm text-slate-500">ASD-STE100 Issue 9 compliance check against working directory DMs</p>
        </div>
        <Select value={selectedProjectId} onValueChange={(v) => { setSelectedProjectId(v); setResults(null); }}>
          <SelectTrigger className="w-[220px]" data-testid="ste-check-project-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
          </SelectContent>
        </Select>
        {projectId && (dirName ? (
          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 gap-1.5 max-w-[240px]" data-testid="wd-chip">
            <FolderOpen className="w-3 h-3 shrink-0" /><span className="truncate font-mono text-xs">{dirName}</span>
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 gap-1.5" data-testid="wd-chip">
            <FolderOpen className="w-3 h-3" /><span className="text-xs">No directory linked</span>
          </Badge>
        ))}
      </div>

      {/* Working Directory + Check Button */}
      <Card className="border-slate-200" data-testid="working-dir-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <FolderOpen className="w-5 h-5 text-slate-400 shrink-0" />
              {!dirHandle ? (
                <div>
                  <p className="text-sm text-slate-600">No working directory linked</p>
                  <p className="text-xs text-slate-400">Link a directory containing DMC-*.xml files</p>
                </div>
              ) : (
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="flex items-center gap-1.5 px-2 py-1 text-xs">
                      <FolderSync className="w-3 h-3 text-violet-600" />
                      <span className="font-mono text-xs truncate max-w-[200px]">{dirName}</span>
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-[10px]">{dmFiles.length} DM files</Badge>
                    {scanningDir && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {!dirHandle ? (
                <Button variant="outline" size="sm" onClick={linkDirectory} data-testid="link-dir-btn">
                  <FolderOpen className="w-4 h-4 mr-2" /> Link Working Directory
                </Button>
              ) : (
                <>
                  {dmFiles.length === 0 && (
                    <Button variant="outline" size="sm" onClick={refreshDir} className="gap-1.5 text-blue-600" data-testid="regrant-btn">
                      <RefreshCw className="w-3.5 h-3.5" /> Grant Access
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={refreshDir} disabled={scanningDir} data-testid="refresh-dir-btn">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={unlinkDir} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="unlink-dir-btn">
                    <XIcon className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}

              <Button onClick={runCheck}
                disabled={checking || !dirHandle || dmFiles.length === 0}
                className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="run-ste-check-btn">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Check STE Compliance
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-12 gap-4 flex-1" style={{ height: 'calc(100vh - 280px)' }}>
        {/* LEFT: DM File List */}
        <div className="col-span-4 overflow-hidden flex flex-col">
          <Card className="border-slate-200 flex flex-col flex-1 overflow-hidden" data-testid="dm-files-card">
            <div className="p-3 border-b border-slate-200 flex items-center justify-between shrink-0">
              <h3 className="text-sm font-semibold text-slate-700">DM Files in Directory</h3>
              <Badge className="bg-slate-100 text-slate-600 text-[10px]">{dmFiles.length}</Badge>
            </div>
            <CardContent className="p-0 flex-1 overflow-y-auto">
              {dmFiles.length === 0 ? (
                <div className="p-6 text-center">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">
                    {dirHandle ? 'No DMC-*.xml files found' : 'Link a working directory to scan'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {dmFiles.map((dm, i) => {
                    const dmResult = results?.results?.find(r => r.dmc === dm.name.replace(/\.xml$/i, ''));
                    return (
                      <div key={i} className="px-3 py-1.5 flex items-center gap-2" data-testid={`dm-file-${i}`}>
                        {dmResult ? (
                          dmResult.compliant
                            ? <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                            : <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                        ) : (
                          <FileText className="w-3 h-3 text-slate-300 shrink-0" />
                        )}
                        <span className="text-[11px] font-mono text-slate-600 truncate">{dm.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: Results */}
        <div className="col-span-8 overflow-y-auto space-y-4">
          {/* Overall summary */}
          {overall && (
            <Card className={`border-2 ${overall.compliance_pct === 100 ? 'border-emerald-300 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/30'}`}
              data-testid="ste-overall-summary">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {overall.compliance_pct === 100
                    ? <CheckCircle className="w-10 h-10 text-emerald-500 shrink-0" />
                    : <AlertTriangle className="w-10 h-10 text-amber-500 shrink-0" />}
                  <div>
                    <p className={`text-xl font-bold ${overall.compliance_pct === 100 ? 'text-emerald-800' : 'text-amber-800'}`}>
                      {overall.compliance_pct}% Compliant
                    </p>
                    <p className="text-sm text-slate-600">
                      {overall.compliant_dms}/{overall.total_dms} DMs pass all STE Issue 9 rules
                    </p>
                  </div>
                  <div className="ml-auto grid grid-cols-3 gap-3 text-center">
                    <div className="px-3 py-2 bg-white/70 rounded-lg">
                      <p className="text-lg font-bold text-slate-800">{overall.total_dms}</p>
                      <p className="text-[10px] text-slate-500">Total DMs</p>
                    </div>
                    <div className="px-3 py-2 bg-white/70 rounded-lg">
                      <p className="text-lg font-bold text-emerald-600">{overall.compliant_dms}</p>
                      <p className="text-[10px] text-slate-500">Compliant</p>
                    </div>
                    <div className="px-3 py-2 bg-white/70 rounded-lg">
                      <p className="text-lg font-bold text-red-600">{overall.total_violations}</p>
                      <p className="text-[10px] text-slate-500">Violations</p>
                    </div>
                  </div>
                </div>
                {results?.technical_nouns_count != null && (
                  <p className="text-xs text-slate-500 mt-2">
                    Checked with {results.technical_nouns_count} project technical noun{results.technical_nouns_count !== 1 ? 's' : ''}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Per-DM results */}
          {results?.results?.length > 0 && (
            <Card className="border-slate-200" data-testid="ste-dm-results">
              <div className="p-3 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700">Per-DM Results</h3>
                <p className="text-[11px] text-slate-400">Click a row to expand rule details and violations</p>
              </div>
              <CardContent className="p-0">
                {results.results.map((dm, i) => (
                  <DMResultRow key={dm.dmc} dm={dm} index={i} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Empty state */}
          {!results && (
            <Card className="border-dashed border-2 border-slate-200 flex items-center justify-center" style={{ minHeight: '300px' }}>
              <CardContent className="text-center py-12">
                <ShieldCheck className="w-14 h-14 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">Ready to Check</p>
                <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
                  Link a working directory containing DM XML files, then click "Check STE Compliance" to validate against ASD-STE100 Issue 9 rules.
                </p>
                <div className="mt-4 text-xs text-slate-400 space-y-0.5">
                  <p>Rules checked: Vocabulary, Technical Nouns, Sentence Length,</p>
                  <p>One Topic Per Sentence, Paragraph Length, Active Voice, No '-ing' Starts</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default STECheckPage;
